import { ArrowBack } from '@mui/icons-material';
import { Divider, Step, StepLabel, Stepper } from '@mui/material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { paths } from 'src/paths';
import {
  resetSaveMiscDocumentAction,
  saveMiscDocumentAction,
} from 'src/redux/actions/documentationAction';
import { getSinglePatient } from 'src/redux/actions/patientAction';
import { bucketWetSignatureDocs } from 'src/utils/signaturePermissions';
import PhysicianDocTable from './physician-doc-table';
import PreviewSignatureDocument from './preview-signature-document';
import UploadPhysicianDoc from './upload-physician-doc';
import AttachmentType from './Attachment-type';
import UploadHospitalRecords from './upload-hospital-records';

const PhysicianSignDocument = () => {
  const location = useLocation();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { patients, patientDetails, episodeOfCare, details } = paths;
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const getAllSubmittedDocumentResponse = useSelector(
    (state: any) => state.getAllSubmittedDocumentReducer
  );

  useEffect(() => {
    dispatch(
      getSinglePatient({
        patientId: getPatientId,
      })
    );
  }, [getPatientId]);

  const [selectedAttachmentType, setSelectedAttachmentType] = useState('');
  const steps = selectedAttachmentType === 'HospitalRecord' ? ['Attachment Type', 'Upload attachment'] : ['Attachment Type', 'Select Clinical Document', 'Upload attachment', 'PREVIEW'];
  const [activeStep, setActiveStep] = useState(0);
  const [physicianSignatureDoc, setPhysicianSignatureDoc] = useState<any>([]);
  const [supervisorSignatureDoc, setSupervisorSignatureDoc] = useState<any>([]);

  const sortByDateOfServiceAndGroup = (completedDocs: any) => {
    const groupedDocuments = completedDocs?.reduce((groups: any, document: any) => {
      const { formType } = document;
      if (!groups[formType]) {
        groups[formType] = [];
      }
      groups[formType].push(document);
      return groups;
    }, {});

    const sortedDocuments = Object?.values(groupedDocuments)?.flatMap(
      (group: any) =>
        group?.sort(
          (a: any, b: any) =>
            Number(new Date(a?.dateOfService)) - Number(new Date(b?.dateOfService))
        )
    );
    return sortedDocuments;
  };

  useEffect(() => {
    try {
      // Bucket into the physician / supervisor wet-signature upload lists from
      // signatureContext. The two lists are independent — a both-required eval belongs
      // in both — see bucketWetSignatureDocs (THIN-7575).
      const { physicianSignatureDocs, supervisorSignatureDocs } = bucketWetSignatureDocs(
        getAllSubmittedDocumentResponse?.response?.content ?? []
      );

      setPhysicianSignatureDoc(sortByDateOfServiceAndGroup(physicianSignatureDocs));
      setSupervisorSignatureDoc(sortByDateOfServiceAndGroup(supervisorSignatureDocs));
    } catch (error) {
      console.error('error', error);
    }
  }, [getAllSubmittedDocumentResponse]);

  const handleNext = () => {
    if (isLastStep()) {
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const isLastStep = () => {
    return activeStep === steps.length - 1;
  };

  const handleNextSelectPhysician = () => {
    setUploadedDoc({});
    handleNext();
  };

  const [uploadedDoc, setUploadedDoc] = useState<any>({});
  const [selectedDocument, setSelectedDocument] = useState<any>('');
  const [otherType, setOtherType] = useState('');

  const getTherapistProfileData = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );

  const saveMiscDocResponse = useSelector((state: any) => state.saveMiscellaneousDocumentsReducer);

  const handleMultiFileUpload = async () => {
    if(!Array.isArray(uploadedDoc)) {
      return;
    }

    const formData = new FormData();
    formData.append('therapistId', getTherapistProfileData?.providerId);
    formData.append('episodeOfCareId', location?.state?.episodeOfCareId);
    
    uploadedDoc.forEach((file) => {
      const safeCharactersRegex = /[^a-zA-Z0-9.\-_]/g; 
      const sanitizedName = file.name.replace(safeCharactersRegex, '_');
      
      // Create a new File object with the sanitized name
      const sanitizedFile = new File([file], sanitizedName, { type: file.type });
      
      formData.append(`files`, sanitizedFile);
      formData.append(`documentName`, sanitizedName);
    })

    dispatch(
      saveMiscDocumentAction({
        data: formData,
        param: {
          type: 'HospitalRecord',
          uuid: selectedDocument?.uuid,
        },
      })
    );
  }


  const handleUploadSignFile = () => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const formData = new FormData();

      formData.append('files', uploadedDoc);
      formData.append('therapistId', getTherapistProfileData?.providerId);
      formData.append('episodeOfCareId', location?.state?.episodeOfCareId);
      formData.append('documentName', uploadedDoc?.name);

      if (selectedAttachmentType === 'PhysicianSignature') {
        dispatch(
          saveMiscDocumentAction({
            data: formData,
            param: {
              type: 'PHYSICIAN_SIGNATURE',
              uuid: selectedDocument?.uuid,
            },
          })
        );
      } else if (selectedAttachmentType === 'SUPERVISOR_SIGNATURE') {
        dispatch(
          saveMiscDocumentAction({
            data: formData,
            param: {
              type: 'SUPERVISOR_SIGNATURE',
              uuid: selectedDocument?.uuid,
            },
          })
        );
      } else {
        dispatch(
          saveMiscDocumentAction({
            data: formData,
            param: {
              type: !!otherType ? otherType : 'OTHER',
            },
          })
        );
      }
    };

    reader.readAsDataURL(uploadedDoc);
  };

  const handleSelectedAttachmentType = () => {
    setSelectedDocument('');
    handleNext();
  };

  const renderComponent = (stepIndex: any, isHospitalDocument: boolean) => {
    if(isHospitalDocument) {
      switch (stepIndex) {
        case 0:
          return (
            <AttachmentType
              activeStep={activeStep}
              handleSelectedAttachmentType={handleSelectedAttachmentType}
              handleBack={handleBack}
              handelNavigateBack={handelNavigateBack}
              selectedAttachmentType={selectedAttachmentType}
              setSelectedAttachmentType={setSelectedAttachmentType}
              setOtherType={setOtherType}
              otherType={otherType}
            />
          );
        case 1:
          return (
            <UploadHospitalRecords
              activeStep={activeStep}
              setUploadedDoc={setUploadedDoc}
              handleBack={handleBack}
              handleMultiFileUpload={handleMultiFileUpload}
              files={uploadedDoc}
              handelNavigateBack={handelNavigateBack}
            />
          );
        default:
          return null;
      }
    }
    
    switch (stepIndex) {
      case 0:
        return (
          <AttachmentType
            activeStep={activeStep}
            handleSelectedAttachmentType={handleSelectedAttachmentType}
            handleBack={handleBack}
            handelNavigateBack={handelNavigateBack}
            selectedAttachmentType={selectedAttachmentType}
            setSelectedAttachmentType={setSelectedAttachmentType}
            setOtherType={setOtherType}
            otherType={otherType}
          />
        );
      case 1:
        return (
          <PhysicianDocTable
            activeStep={activeStep}
            physicianSignatureDoc={
              selectedAttachmentType === 'PhysicianSignature'
                ? physicianSignatureDoc
                : selectedAttachmentType === 'SUPERVISOR_SIGNATURE'
                ? supervisorSignatureDoc
                : []
            }
            handleNextSelectPhysician={handleNextSelectPhysician}
            handleBack={handleBack}
            setSelectedDocument={setSelectedDocument}
            selectedDocument={selectedDocument}
            handelNavigateBack={handelNavigateBack}
            selectedAttachmentType={selectedAttachmentType}
          />
        );
      case 2:
        return (
          <UploadPhysicianDoc
            activeStep={activeStep}
            setUploadedDoc={setUploadedDoc}
            handleBack={handleBack}
            handleNext={handleNext}
            uploadedDoc={uploadedDoc}
            handelNavigateBack={handelNavigateBack}
          />
        );
      case 3:
        return (
          <PreviewSignatureDocument
            activeStep={activeStep}
            handleUploadSignFile={handleUploadSignFile}
            handleBack={handleBack}
            uploadedDoc={uploadedDoc}
            handelNavigateBack={handelNavigateBack}
          />
        );

      default:
        return null;
    }
  };

  const handelNavigateBack = () => {
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}#miscellaneous-documents-header`, {
      state: {
        episodeOfCareId: location?.state?.episodeOfCareId,
        getPatientId,
      },
    });
  };

  useEffect(() => {
    if (!saveMiscDocResponse.isDocumentSave && saveMiscDocResponse.response === null) return;
    handelNavigateBack();
    dispatch(resetSaveMiscDocumentAction());
  }, [saveMiscDocResponse]);

  return (
    <>
      <Seo title="Dashboard: Customer Details" />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 2,
          height: '100%',
        }}
      >
        <Container
          sx={{ height: '100%' }}
          maxWidth="xl"
        >
          <Stack
            sx={{ height: '100%' }}
            spacing={3}
          >
            <Stack spacing={4}>
              <div>
                <Link
                  color="text.primary"
                  component={RouterLink}
                  sx={{
                    alignItems: 'center',
                    display: 'inline-flex',
                  }}
                  href={`${patients}/${patientDetails}${episodeOfCare}${details}`}
                  state={{
                    episodeOfCareId: location?.state?.episodeOfCareId,
                    getPatientId,
                  }}
                  // onClick={handelNavigateBack}
                  underline="hover"
                >
                  <SvgIcon sx={{ mr: 1 }}>
                    <ArrowBack sx={{ color: '#6C737F' }} />
                  </SvgIcon>

                  <Typography variant="subtitle1">
                    Back to Episode of Care Details -
                    {` ${patientData?.lastName ?? ''} ${patientData?.firstName ?? ''}`}
                  </Typography>
                </Link>
              </div>

              <Stack
                alignItems="center"
                direction="row"
                spacing={2}
              >
                <Stack
                  spacing={1}
                  pl={1}
                >
                  <Typography variant="h4">Attach New Document </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Stack
              width={'100%'}
              sx={{ height: '100%' }}
              flexDirection={'row'}
              mt={5}
            >
              <Box
                // mt={5}
                width={'20%'}
                // sx={{ border: '1px solid orange' }}
              >
                <Stepper
                  orientation="vertical"
                  activeStep={activeStep}
                >
                  {steps.map((label: any) => {
                    const labelProps: {
                      optional?: React.ReactNode;
                      error?: boolean;
                    } = {};

                    return (
                      <Step key={label}>
                        <StepLabel {...labelProps}>
                          <Typography variant="overline">{label}</Typography>
                        </StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              </Box>

              <Divider orientation="vertical" />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  // border: '1px solid red',
                  // mt: 2,
                  height: '100%',
                }}
                width={'80%'}
                // mt={5}
              >
                {renderComponent(activeStep, selectedAttachmentType === 'HospitalRecord')}
              </Box>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default PhysicianSignDocument;
